/**
 * 將圖片縮放至最長邊 ≤ maxEdge（維持比例），並限制輸出大小。
 * preserveTransparency 開啟時，PNG／WebP 保留原檔或縮圖為 PNG，避免透明底轉 JPEG 後變黑。
 * @param {File} file
 * @param {{ maxEdge: number, maxBytes: number, preserveTransparency?: boolean }} opts
 * @returns {Promise<File>}
 */
export async function resampleImageFile(file, { maxEdge, maxBytes, preserveTransparency = false }) {
  if (!maxEdge || maxEdge < 1) return file;

  const url = URL.createObjectURL(file);
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('無法讀取圖片'));
    i.src = url;
  });
  URL.revokeObjectURL(url);

  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  if (!nw || !nh) throw new Error('圖片尺寸無效');

  let tw = nw;
  let th = nh;
  const longest = Math.max(nw, nh);
  if (longest > maxEdge) {
    const s = maxEdge / longest;
    tw = Math.max(1, Math.round(nw * s));
    th = Math.max(1, Math.round(nh * s));
  }

  const downscaled = tw !== nw || th !== nh;
  const preserveAlpha = preserveTransparency && ['image/png', 'image/webp'].includes(file.type);
  const needReencode = downscaled || (!preserveAlpha && file.type !== 'image/jpeg') || file.size > maxBytes;
  if (!needReencode) return file;

  const canvas = document.createElement('canvas');
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, tw, th);

  const outputType = preserveAlpha ? 'image/png' : 'image/jpeg';
  const blob = await new Promise((resolve, reject) => {
    const tryQ = (q) => {
      canvas.toBlob(
        (b) => {
          if (!b) {
            reject(new Error('壓縮失敗'));
            return;
          }
          if (b.size <= maxBytes) {
            resolve(b);
            return;
          }
          // PNG 不使用 JPEG 的品質參數；超過上限時保留透明度並明確拒絕。
          if (preserveAlpha || q <= 0.5) {
            reject(new Error(`縮圖後仍超過 ${(maxBytes / (1024 * 1024)).toFixed(1)}MB，請改用較小的原始檔`));
            return;
          }
          tryQ(Math.round((q - 0.1) * 10) / 10);
        },
        outputType,
        q
      );
    };
    tryQ(0.92);
  });

  const newName = file.name.replace(/\.[^.]+$/, preserveAlpha ? '.png' : '.jpg');
  return new File([blob], newName, { type: outputType });
}

/** LINE 圖片等需要 JPEG 的既有呼叫端維持原有行為。 */
export async function resampleImageFileToJpeg(file, options) {
  return resampleImageFile(file, { ...options, preserveTransparency: false });
}
